import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { CookiePolicy } from '@ott/cookie-policy';
import { Container } from '@ott/grid';
import { authActions } from '@ott/solar-auth';
import Footer from '@ott/solar-footer';
import Header, { HEADER_THEME_DARK } from '@ott/solar-header';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';
import { RootState } from 'src/redux/store';

import SupportChat from 'src/components/blocks/SupportChat';

import metrics from 'src/utility/metrics';

import { COOKIE_POLICY_LINK } from 'src/constants/common';

import useIsWebview from 'src/hooks/useIsWebview';
import useTypedSelector from 'src/hooks/useTypedSelector';

import BeansBlock from './blocks/BeansBlock';
import CarouselBlock from './blocks/CarouselBlock';
import DownloadBlock from './blocks/DownloadBlock';
import MainScreen from './blocks/MainScreen';
import PaymentOptions from './blocks/PaymentOptions';
import PlanetsSlider from './blocks/PlanetsSlider';
import SupportBlock from './blocks/SupportBlock';

import styles from './MobileAppPage.scss';

const MobileAppPage = () => {
  const auth = useTypedSelector(getAuthSelector);
  const ssr: boolean = useTypedSelector((state: RootState) => state.common.ssr);
  const isWebview = useIsWebview();
  const dispatch = useDispatch();
  const history = useHistory();
  const isAuthorized = auth?.data?.auth;

  const [newMessages, setNewMessages] = useState(0);
  const [isSupportChatVisible, setIsSupportChatVisible] = useState(false);
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);

  useEffect(() => {
    metrics('SOLAR_LANDING_APPPAGE_ENTRY');
  }, []);

  useEffect(() => {
    if (auth.infoStatus.initial) {
      dispatch(authActions.getBasicUserInfo());
    }
  }, []);

  useEffect(() => {
    const rootElement = document.getElementById('root');

    if (rootElement) {
      rootElement.style.overflowX = 'hidden';
    }
  }, []);

  const handleNewMessages = useCallback((count: number) => {
    setNewMessages(count);
  }, []);

  const handleOpenSupportChat = useCallback((isOpen: boolean) => {
    setIsSupportChatOpen(isOpen);
  }, []);

  const onSupportChatVisible = useCallback((isVisible: boolean) => {
    setIsSupportChatVisible(isVisible);
  }, []);

  if (ssr) {
    return null;
  }

  return (
    <>
      {!isWebview && (
        <>
          <CookiePolicy cookieLink={COOKIE_POLICY_LINK} />
          <Header
            theme={HEADER_THEME_DARK}
            history={history}
            isTransparent={true}
            supportNewMessagesCount={newMessages}
            onOpenSupportChat={handleOpenSupportChat}
            isSupportChatVisible={isSupportChatVisible}
          />
        </>
      )}
      <Container className={styles.container}>
        <MainScreen />
        <CarouselBlock />
        <PaymentOptions />
        <PlanetsSlider />
        <SupportBlock />
        <BeansBlock />
        <DownloadBlock />
      </Container>
      {!isWebview && <Footer />}
      <SupportChat
        isUserAuthorized={isAuthorized}
        isSolar={true}
        isChatButtonHidden={true}
        isOpenFromOutside={isSupportChatOpen}
        onOpenFromOutside={handleOpenSupportChat}
        onReceiveNewMessages={handleNewMessages}
        onCheckAvailability={onSupportChatVisible}
      />
    </>
  );
};

export default MobileAppPage;
